// tanstack store provider for all components
// sets apiconfig based on widget props
// sets user data from on widget props

import React from 'react';
import {Store, useStore} from '@tanstack/react-store';
import createApiPath from '@shared/components/helpers/createApiPath';
import {KeyValueKey, TanstackCommonStore, TanstackStoreApiData} from './types';
import {WidgetBreadcrumb} from '@shared/types/widgetBreadcrumb';
import {WidgetConfig} from '@shared/types/widgetConfig';
import {CONFIG_KEY, WIDGET_BREADCRUMBS_KEY} from '@shared/ducks/config';
import {CODES_KEY} from '@shared/ducks/codes/constants';
import {CMS_TITLE_KEY} from '@shared/ducks/cms';
import {NAVILINKS_KEY} from '@shared/ducks/navilinks';
import {BASENAME_KEY} from '@shared/ducks/basename';
import {ECOSYSTEM_API_KEY} from '@shared/ducks/organization/constants';
import {DEFAULT_LOCALE} from '@shared/config';
import deepMerge from '@shared/helpers/deepmerge';
import {UserDataProps} from '@common/types/user';

const DEFAULT_TANSTACK_STORE_STATE: TanstackCommonStore = {
  apiPath: undefined,
  keyvalue: {},
  api: {}
};

// store initialization
const origtStore = new Store(DEFAULT_TANSTACK_STORE_STATE);
type OurStore = Store<TanstackCommonStore> & {
  originalSetState: (setter: SetterFunction) => void;
  safeSetState?: (setter: SetterFunction, isSafe?: boolean) => void;
};
type SetterFunction = (state: TanstackCommonStore) => TanstackCommonStore;
const tStore: OurStore = Object.assign(origtStore, {
  originalSetState: origtStore.setState
});
const safeSetState = (setter: SetterFunction, isSafe = false) => {
  if (!isSafe) {
    // Detect if some noob is using setState directly
    // eslint-disable-next-line no-console
    console.error(
      'Unsafe setState usage detected! Please use mergeToStore or updateApiData instead to prevent altering the store in undesirable way.'
    );
  }
  tStore.originalSetState(setter);
};
// replace with our wrapped setState to ensure safe usage
Object.assign(tStore, {
  setState: safeSetState,
  safeSetState: safeSetState
});

type Props = {
  children: React.ReactNode;
  locale?: string;
  apiPath?: string | object;
  defaultApi?: string;
  organizationId?: string;
  widgetBreadcrumbs?: WidgetBreadcrumb[];
  basename?: string;
  widgetId?: string;
  config?: WidgetConfig;
  codesApiPath?: string;
  consentsandserviceprovidersApiPath?: string;
  skillRecommendationApiPath?: string;
  cmsPageTitle?: string;
  links?: Record<string, string>;
  user?: UserDataProps;
};

const TanstackStoreProvider = ({
  children,
  apiPath,
  locale,
  defaultApi,
  organizationId,
  widgetBreadcrumbs,
  basename,
  widgetId,
  config,
  codesApiPath,
  consentsandserviceprovidersApiPath,
  skillRecommendationApiPath,
  cmsPageTitle,
  links,
  user
}: Props) => {
  const createdApiPath = createApiPath(
    apiPath ?? '',
    defaultApi ?? '',
    organizationId ?? ''
  );
  mergeToStore({
    widgetId: widgetId,
    apiPath: createdApiPath,
    keyvalue: {
      [CONFIG_KEY]: config,
      [CODES_KEY]: codesApiPath,
      [NAVILINKS_KEY]: links,
      [CMS_TITLE_KEY]: cmsPageTitle,
      [BASENAME_KEY]: basename,
      [WIDGET_BREADCRUMBS_KEY]: widgetBreadcrumbs,
      [ECOSYSTEM_API_KEY]: consentsandserviceprovidersApiPath
    },
    skillRecommendationApiPath: skillRecommendationApiPath,
    api: {
      user: undefined,
      userInfo: undefined
    },
    locale: locale,
    userProps: user
  });

  return <>{children}</>;
};

const updateApiData = (apiData: Record<string, unknown>) => {
  mergeToStore({api: apiData});
};

const mergeToStore = (newData: Record<string, unknown>) => {
  tStore.safeSetState?.(state => mergeFunction(state, newData), true);
};

export const clearApiData = (dataId: keyof TanstackStoreApiData) => {
  tStore.safeSetState?.(state => {
    const newState = {...state};
    if (newState?.api?.[dataId]) {
      delete newState.api[dataId];
    }
    return newState;
  }, true);
};

export const mergeFunction = (
  state: TanstackCommonStore,
  newData: Record<string, unknown>
) => {
  return deepMerge(state, newData);
};

// Let's define some hooks for commonly used state values
const useLocale = () =>
  useStore(tStore, state => state.locale) ?? DEFAULT_LOCALE;

const useKeyValue = (key: KeyValueKey) =>
  useStore(tStore, state => state.keyvalue?.[key]);

const useWidgetLinks = () =>
  useStore(tStore, state => state.keyvalue?.[NAVILINKS_KEY]);

const useWidgetConfig = () =>
  useStore(tStore, state => state.keyvalue?.[CONFIG_KEY]);

const useWidgetBreadcrumbs = () =>
  useStore(tStore, state => state.keyvalue?.[WIDGET_BREADCRUMBS_KEY]);

export {
  TanstackStoreProvider as default,
  tStore,
  useLocale,
  updateApiData,
  mergeToStore,
  useKeyValue,
  useWidgetLinks,
  useWidgetConfig,
  useWidgetBreadcrumbs
};
